import { useEffect, useState } from 'react';
import { parseContainingApp } from './containing_app.ts';

// Best-effort name and version of the operating system the page runs on, for the About page.
//
// Three sources, from most to least reliable:
// 1. The containing app's user agent token (store apps 24.0 and later): exact, and the only way to see
//    the real Android version or to tell an iPad from a Mac.
// 2. User-agent client hints (Chromium browsers, Chrome on Android included): getHighEntropyValues
//    reports the real platform version despite the frozen user agent. It is asynchronous, so
//    refineOperatingSystem fills it in after the synchronous guess.
// 3. The user agent string, frozen at "Mac OS X 10_15_7", "Windows NT 10.0", "Android 10; K" and, from
//    iOS 26 on, "OS 18_7", so those sentinels give a name without a version.

export interface OperatingSystem {
  name: string;
  version?: string;
}

export function parseOperatingSystem(userAgent: string, { maxTouchPoints = 0 } = {}): OperatingSystem {
  const containingApp = parseContainingApp(userAgent);
  if (containingApp) {
    return { name: containingApp.os, version: containingApp.osVersion };
  }

  const ios = userAgent.match(/\b(iPhone|iPad|iPod)\b[^)]*\bOS (\d+)_(\d+)(?:_(\d+))?/);
  if (ios) {
    const version = [ios[2], ios[3], ios[4]].filter(Boolean).join('.');
    // iOS 26 and later report "OS 18_7" (seen on an iOS 26.5 simulator), so that value could be either
    return { name: ios[1] === 'iPad' ? 'iPadOS' : 'iOS', version: version === '18.7' ? undefined : version };
  }

  if (/\bAndroid\b/.test(userAgent)) {
    // Chrome 110 and later freeze the token at "Android 10; K"
    const frozen = /\bAndroid 10; K\b/.test(userAgent);
    return { name: 'Android', version: frozen ? undefined : userAgent.match(/\bAndroid (\d+(?:\.\d+)*)/)?.[1] };
  }

  if (/\bCrOS\b/.test(userAgent)) {
    return { name: 'ChromeOS', version: userAgent.match(/\bCrOS \S+ (\d+(?:\.\d+)*)/)?.[1] };
  }

  if (/\bMacintosh\b/.test(userAgent)) {
    // An iPad asking for the desktop site says Macintosh, but a Mac has no touch screen
    if (maxTouchPoints > 1) {
      return { name: 'iPadOS' };
    }
    const mac = userAgent.match(/\bMac OS X (\d+)[_.](\d+)(?:[_.](\d+))?/);
    const version = mac ? [mac[1], mac[2], mac[3]].filter(Boolean).join('.') : undefined;
    // Safari and Firefox freeze the token at 10.15.7 and 10.15
    return { name: 'macOS', version: version && !version.startsWith('10.15') ? version : undefined };
  }

  const windows = userAgent.match(/\bWindows NT (\d+\.\d+)/);
  if (windows) {
    return { name: 'Windows', version: windowsVersionFromNT(windows[1]) };
  }
  if (/\bWindows\b/.test(userAgent)) {
    return { name: 'Windows' };
  }

  if (/\bLinux\b/.test(userAgent)) {
    return { name: 'Linux' };
  }

  return { name: 'Unknown' };
}

// "Windows NT 10.0" is frozen and means Windows 10 or 11; client hints can tell them apart
function windowsVersionFromNT(nt: string): string | undefined {
  switch (nt) {
    case '6.1':
      return '7';
    case '6.2':
      return '8';
    case '6.3':
      return '8.1';
    default:
      return undefined;
  }
}

// The synchronous best guess for this page (sources 1 and 3 above)
export function getOperatingSystem(): OperatingSystem {
  if (typeof navigator === 'undefined') {
    return { name: 'Unknown' };
  }
  return parseOperatingSystem(navigator.userAgent, { maxTouchPoints: navigator.maxTouchPoints ?? 0 });
}

interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<{ platform?: string; platformVersion?: string }>;
}

// Fills in the version from user-agent client hints when the user agent could not say (source 2 above);
// returns `current` unchanged when it already has a version or the browser offers no hints
export async function refineOperatingSystem(current: OperatingSystem): Promise<OperatingSystem> {
  if (current.version || typeof navigator === 'undefined') {
    return current;
  }
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!userAgentData) {
    return current;
  }
  try {
    const { platform, platformVersion } = await userAgentData.getHighEntropyValues(['platformVersion']);
    if (!platformVersion) {
      return current;
    }
    switch (platform) {
      case 'Windows': {
        // platformVersion reports the Windows build lineage: 13 and up is Windows 11, 1 to 12 is Windows 10
        const major = Number(platformVersion.split('.')[0]);
        return { name: 'Windows', version: major >= 13 ? '11' : major >= 1 ? '10' : undefined };
      }
      case 'macOS':
        return { name: 'macOS', version: trimVersion(platformVersion) };
      case 'Android':
        return { name: 'Android', version: trimVersion(platformVersion) };
      case 'Chrome OS':
      case 'ChromeOS':
        return { name: 'ChromeOS', version: trimVersion(platformVersion) };
      default:
        return current;
    }
  } catch {
    return current;
  }
}

// '15.6.0' -> '15.6', '14.0.0' -> '14'
function trimVersion(version: string): string {
  const parts = version.split('.');
  while (parts.length > 1 && parts[parts.length - 1] === '0') {
    parts.pop();
  }
  return parts.join('.');
}

export function formatOperatingSystem({ name, version }: OperatingSystem): string {
  return version ? `${name} ${version}` : name;
}

// The best available answer: the synchronous guess at once, refined by client hints when they resolve
export function useOperatingSystem(): OperatingSystem {
  const [operatingSystem, setOperatingSystem] = useState(getOperatingSystem);
  useEffect(() => {
    let cancelled = false;
    void refineOperatingSystem(getOperatingSystem()).then((refined) => {
      if (!cancelled) {
        setOperatingSystem(refined);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return operatingSystem;
}
